import React, { useMemo, useState } from 'react';
import { MapContainer, ZoomControl } from 'react-leaflet';
import { Box } from '@chakra-ui/react';
import { map as mapConfig } from '../../app/config';
import { Layers } from '..';
import { MinimapSearch, MinimapStaticMarker, MinimapDraggableMarker, MinimapHint, MinimapGeolocator } from './components';

const MinimapControl = ({
  initialCenter,
  onNewSearch,
  onChangeLatLng,
  hintText,
  isMapDraggable = true,
  isMapZoomable = true,
  isMarkerDraggable = false,
  isSearchable = false,
  markerType = 'default',
  withGeolocator = false,
  handleGeolocate,
  boxProps = {},
  zoom = 15,
  height = 300,
}) => {
  const [ draggableMarkerPosition, setDraggableMarkerPosition ] = useState(null);

  const minimap = useMemo(
    () => (
      <MapContainer
        style={{ height }}
        center={initialCenter}
        zoom={zoom}
        minZoom={mapConfig.defaultZoomLevel}
        maxZoom={mapConfig.maxZoomLevel}
        dragging={isMapDraggable}
        doubleClickZoom
        scrollWheelZoom
        maxBounds={mapConfig.maxBoundsWorld}
        worldCopyJump
        maxBoundsViscosity={1.0}
        zoomControl={false}
        attributionControl={false}
        className="minimap-container"
      >
        <Layers position={withGeolocator ? "bottomright" : "bottomleft"} />

        {isMapZoomable && (
          <ZoomControl position="bottomright" />
        )}

        {withGeolocator && (
          <MinimapGeolocator
            position="bottomleft"
            zoomTo={15}
            onAfterLocate={async (coords) => {
              if (coords.latitude && coords.longitude) {
                const latng = { lat: coords.latitude, lng: coords.longitude };
                await handleGeolocate(latng);
                setDraggableMarkerPosition(latng);
              }
            }}
          />
        )}

        {isSearchable && (
          <MinimapSearch
            position="topright"
            onLocationSearch={(locationResult) => {
              if (locationResult) {
                onNewSearch(locationResult);
                if (isMarkerDraggable) {
                  setDraggableMarkerPosition(null);
                }
              }
            }}
          />
        )}

        {isMarkerDraggable && (
          <MinimapDraggableMarker
            markerPosition={draggableMarkerPosition || initialCenter}
            onMarkerDrag={(newPosition) => {
              setDraggableMarkerPosition(newPosition);
              onChangeLatLng(newPosition);
            }}
          />
        )}

        {!isMarkerDraggable && (
          <MinimapStaticMarker
            markerPosition={initialCenter}
            zoom={zoom}
            markerType={markerType}
          />
        )}

        {hintText && (
          <MinimapHint hintText={hintText} />
        )}
      </MapContainer>
    ),
    [
      height,
      initialCenter,
      zoom,
      isMapDraggable,
      isMapZoomable,
      isSearchable,
      onNewSearch,
      onChangeLatLng,
      isMarkerDraggable,
      hintText,
    ],
  );

  return (
    <Box
      w="full"
      userSelect="none"
      className="leaflet-control"
      id="minimap-control"
      {...boxProps}
    >
      {minimap}
    </Box>
  );
};

export default MinimapControl;
